import {
  PaymentMethodErrorCode,
  SavedCard,
} from '../../services/subscription-payment-method.service';

/**
 * CWS-1536: how a saved card and a Payment Method refusal read in the Payment method pop-ups.
 */
const BRANDS: Record<string, { name: string; badge: string }> = {
  visa: { name: 'Visa', badge: 'VISA' },
  mastercard: { name: 'Mastercard', badge: 'MC' },
  amex: { name: 'Amex', badge: 'AMEX' },
  discover: { name: 'Discover', badge: 'DISC' },
  diners: { name: 'Diners Club', badge: 'DC' },
  jcb: { name: 'JCB', badge: 'JCB' },
  unionpay: { name: 'UnionPay', badge: 'UPAY' },
};

function brandOf(card: Pick<SavedCard, 'brand'>): { name: string; badge: string } {
  return BRANDS[(card.brand || '').toLowerCase()] ?? { name: 'Card', badge: 'CARD' };
}

/** "Visa •••• 4242" */
export function cardLabel(card: Pick<SavedCard, 'brand' | 'last4'>): string {
  const name = brandOf(card).name;
  return card.last4 ? `${name} •••• ${card.last4}` : name;
}

/**
 * The toast after a renewal card is changed, the same from the Subscriptions tab and the lane
 * pop-ups: "Card updated for Next Renewal" / "Visa •••• 4242 · ABC123".
 */
export function renewalCardUpdatedToast(
  card: Pick<SavedCard, 'brand' | 'last4'>,
  membership: { plate?: string | null; planName?: string | null } | null
): { title: string; detail: string } {
  return {
    title: 'Card updated for Next Renewal',
    detail: `${cardLabel(card)} · ${membership?.plate || membership?.planName || 'this membership'}`,
  };
}

/**
 * The Payment method pop-up of an imported membership: its payments still go through the vendor it
 * was imported from, and a card saved here is only kept, never charged, while that lasts.
 */
export function importedPaymentsNotice(billedBy: string): string {
  return `Current transactions for this membership are processed through ${billedBy}. Cards saved here are not charged while ${billedBy} bills it.`;
}

/** "VISA", "MC", "AMEX"... for the brand tile. */
export function cardBadge(card: Pick<SavedCard, 'brand'>): string {
  return brandOf(card).badge;
}

/** "08/28", or null when Stripe gave no expiry. */
function expiryDigits(card: Pick<SavedCard, 'exp_month' | 'exp_year'>): string | null {
  if (!card.exp_month || !card.exp_year) {
    return null;
  }
  return `${String(card.exp_month).padStart(2, '0')}/${String(card.exp_year).slice(-2)}`;
}

/** "Expires 08/28", or "Expired 08/24" for a card Stripe would decline at renewal. */
export function cardExpiryLabel(
  card: Pick<SavedCard, 'exp_month' | 'exp_year' | 'is_expired'>
): string {
  const digits = expiryDigits(card);
  return digits ? `${card.is_expired ? 'Expired' : 'Expires'} ${digits}` : '';
}

/**
 * "Card on file": "Visa •••• 4242 · 08/28", or "… · Expired 08/24". The expiry tells apart
 * cards with the same last 4, as a reissued card has.
 */
export function cardWithExpiryLabel(
  card: Pick<SavedCard, 'brand' | 'last4' | 'exp_month' | 'exp_year' | 'is_expired'>
): string {
  const digits = expiryDigits(card);
  if (!digits) {
    return cardLabel(card);
  }
  const expiry = card.is_expired ? `Expired ${digits}` : digits;
  return `${cardLabel(card)} · ${expiry}`;
}

const REFUSALS: Partial<Record<PaymentMethodErrorCode, string>> = {
  imported_membership:
    'This membership is billed by its vendor, so its card cannot be changed here.',
  way_app_membership:
    'This membership is billed through the Way app. The customer can change the card there.',
  billing_not_linked: 'This membership is not billed to a card, so there is no card to change.',
  membership_not_active: 'Only an active membership’s renewal card can be changed.',
  cancellation_scheduled: 'This membership is set to cancel, so it will not renew on a card.',
  payment_method_disabled: 'Payment method is not turned on.',
  subscription_not_found: 'This membership could not be found. Close and reopen the customer.',
  stripe_unavailable: 'Could not reach the payment processor. Try again.',
  card_not_found: 'That card is no longer on file. Choose another card.',
  card_expired: 'That card has expired. Choose another card or add a new one.',
  card_change_in_progress:
    'Someone else is changing this membership’s card right now. Try again in a moment.',
  not_imported_membership: 'This membership is billed by Way. Change its renewal card instead.',
  shop_not_linked: 'A card can be saved once this member has visited one of your shops.',
  shop_not_connected: 'This shop is not connected to Stripe, so a card cannot be saved here.',
};

export function paymentMethodErrorCopy(code: PaymentMethodErrorCode | null): string {
  if (!code) {
    return '';
  }
  return REFUSALS[code] ?? 'Something went wrong. Try again.';
}
